import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ApiDurationType, ApiPackageDetails, ApiPackageModel, ApiTravelerType } from '@ibe/api';
import { itemDetailEvent, LoggerFactory } from '@ibe/services';
import { Element as ScrollTo } from 'react-scroll';
import { useTheme } from '@emotion/react';
import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState, JSX } from 'react';
import { useMeasure, useMount } from 'react-use';
import { Alert, Button, Col, Row, Fade } from 'reactstrap';
import fallback from '../../../_Translations/generated/package-details.en.t.json';
import Keys from '../../../_Translations/generated/package-details.en.t.json.keys';
import DateDisplay from '../../Components/UI/DateDisplay/DateDisplay';
import { Nbsp } from '../../Components/UI/Nbsp';
import { Price } from '../../Components/UI/Price/Price';
import { Slider, withMedia } from '../../Components/UI/Slider/Slider';
import useTranslation from '../../Hooks/useTranslation';
import PackageCustomizerUtil from '../../Modules/Customizer/PackageCustomizer/PackageCustomizerUtil';
import PriceOptionPanel from '../../Modules/Customizer/PriceDisplay/PriceOptionPanel';
import PackageDetailsTabs from './PackageDetailsTabs';
import ScrollerService from '../../Services/ScrollerService';
import useMediaQuery, { MEDIAQUERY_DEFAULTS } from '../../Hooks/useMediaQuery';
import useConfig from '../../Hooks/useConfig';
import useWindow from '../../Hooks/useWindow';

const logger = LoggerFactory.get('PackageDetails.tsx');

export type PackageDetailsSelection = {
  packageId: string;
  detailId: string;
  travelDateId: string;
};

export interface PackageDetailsProps {
  packageDetailId: string;
  travelDateId: string;
  item: ApiPackageModel;
  onSelect?: (s: PackageDetailsSelection) => void;
  showTotalPriceOnly?: boolean;
}

export const PackageDetails = observer(function PackageDetails(
  props: PackageDetailsProps
): JSX.Element {
  const window = useWindow();
  const { t } = useTranslation('package-details', fallback, Keys);
  const theme = useTheme();
  const config = useConfig();
  const sectionRef = useRef<HTMLTableSectionElement>(null);

  const { item, onSelect, packageDetailId, travelDateId, showTotalPriceOnly } = props;
  const travelCandidateId = travelDateId;
  const category = item.category?.description || '';
  const { media } = item;
  const selectedOptions = 'selectedOptions';
  const isDesktop = useMediaQuery({ type: 'min', query: MEDIAQUERY_DEFAULTS.md });
  const isTablet = useMediaQuery({ type: 'min', query: MEDIAQUERY_DEFAULTS.sm });

  useMount(() => {
    itemDetailEvent.broadcast({ item });
  });

  useEffect(() => {
    const getOffset = (): number => {
      const {
        headerScrollOffset: { mobile, tablet, desktop }
      } = config;
      let offset;
      if (isDesktop && desktop) {
        offset = desktop;
      } else if (isTablet && tablet) {
        offset = tablet;
      } else {
        offset = mobile || 0;
      }
      return offset;
    };
    if (sectionRef.current) {
      window?.scrollTo({ top: sectionRef.current.getBoundingClientRect().top - getOffset() });
    }
  }, [sectionRef, config, isDesktop, isTablet]);

  let travelDate;
  if (item.travelDates) {
    travelDate = item.travelDates.find(d => d.id === travelCandidateId);
  }

  let packageDetailsValue: ApiPackageDetails | null | undefined = null;
  if (item.packageDetails) {
    packageDetailsValue = item.packageDetails.find(d => d.id === packageDetailId);
  }

  logger.log('PackageDetails', item, travelDate, packageDetailsValue);

  const [packageDetails, setPackageDetails] = useState(packageDetailsValue);

  const [selectedOptionId, setSelectedOption] = useState(travelCandidateId);

  const setTravelDate = (value: string): void => {
    setSelectedOption(value);
    if (item.packageDetails) {
      const newPackageDetail = item.packageDetails.find(d => d.travelDateId === value);
      if (newPackageDetail) {
        setPackageDetails(newPackageDetail);
      }
    }
  };

  const gotoCustomizer = (): void => {
    if (onSelect && packageDetails && selectedOptionId) {
      onSelect({
        packageId: item.id,
        detailId: packageDetails.id,
        travelDateId: selectedOptionId
      });
    }
  };

  if (!packageDetails || !item.packageDetails || item.packageDetails.length === 0) {
    return (
      <div>
        <Alert color="danger">
          Could not find 1:1 match in {item.packageDetails ? item.packageDetails.length : 0}{' '}
          PackageDetails for given packageId:
          <br />
          <span style={{ fontSize: '11px', wordWrap: 'break-word' }}>{packageDetailId}</span>
        </Alert>
        <Button onClick={(): void => gotoCustomizer()}>Continue to customizer</Button>
      </div>
    );
  }

  const duration = packageDetails.packageDurationInfo;
  const hotelDuration = packageDetails.includedHotelDurationInfo;
  const paxPrice = packageDetails.priceDetails.find(d => d.travelerType === ApiTravelerType.ADULT)
    ?.price;

  const totalPrice = packageDetails.price;

  const allOptions = item.travelDates;
  const [itemsRenderCount, setItemsRenderCount] = useState(5);
  const [paginationSize] = useState(5);

  let optionsPerPageDefaultValue = 0;
  if (allOptions) {
    if (allOptions.length > paginationSize) {
      optionsPerPageDefaultValue = paginationSize;
    } else {
      optionsPerPageDefaultValue = allOptions.length;
    }
  }
  const [optionsPerPage, setOptionsPerPage] = useState(optionsPerPageDefaultValue);

  const [ref, { width }] = useMeasure<HTMLDivElement>();

  let isSmall = false;

  if (width < 600 && width !== 0) {
    isSmall = true;
  } else if (width > 600 && width !== 0) {
    isSmall = false;
  }

  const selectedOption = PackageCustomizerUtil.getTravelDateById(item, selectedOptionId);

  function calculateNumberOfOptionsToDisplay(isIncrementing: boolean): void {
    if (isIncrementing && allOptions) {
      const amount = optionsPerPage + paginationSize;
      setOptionsPerPage(amount > allOptions.length ? allOptions.length : amount);
    } else {
      const amount = optionsPerPage - paginationSize;
      setOptionsPerPage(amount <= paginationSize ? paginationSize : amount);
    }
  }

  function renderOptions(amount: number): JSX.Element[] {
    const items = [];
    let counter = 0;

    if (allOptions) {
      const options = [...allOptions];
      let count;
      if (amount < 0) {
        count = optionsPerPage > options.length ? options.length : optionsPerPage;
      } else if (amount > options.length) {
        count = options.length;
      } else {
        count = amount;
      }
      for (let i = 0; i < count; i += 1) {
        counter += 1;
        const renderOptionItem = options[i];
        items.push(
          <div className="mb-3 mb-sm-4" key={renderOptionItem.id}>
            <PriceOptionPanel
              travelDate={renderOptionItem}
              isSelected={renderOptionItem.id === selectedOptionId}
              onSelectDate={(id: string): void => setTravelDate(id)}
              key={renderOptionItem.id}
              showTotalPriceOnly={showTotalPriceOnly}
            />
          </div>
        );
      }
    }
    setItemsRenderCount(counter);
    return items;
  }

  const Header = (): JSX.Element => {
    return (
      <>
        <div className="d-flex mb-2 mt-3">
          <h2 className="mr-auto mb-0 d-inline-block">
            <div className="text-muted small iso__category-label">{category}</div>
            {item.name}
          </h2>
          {selectedOption && (
            <Button
              type="button"
              className="my-auto align-self-end d-flex"
              color="primary"
              onClick={(): void => gotoCustomizer()}
            >
              {t(Keys.continue)}
            </Button>
          )}
          {!selectedOption && (
            <Button
              type="button"
              className="my-auto align-self-end d-flex"
              color="primary"
              onClick={(): void => ScrollerService.scrollTo(selectedOptions)}
            >
              {t(Keys.selectOptions)}
            </Button>
          )}
        </div>

        <Row className="mb-2">
          <Col md={8}>
            {packageDetails && (
              <>
                <div className="font-weight-bold">
                  {duration &&
                    t(duration.type === ApiDurationType.NIGHT ? Keys.nights : Keys.days, {
                      duration: duration.duration
                    })}
                  {hotelDuration && (
                    <>
                      {' '}
                      <Nbsp />/<Nbsp />
                      {t(hotelDuration.type === ApiDurationType.NIGHT ? Keys.nights : Keys.days, {
                        duration: hotelDuration.duration
                      })}
                    </>
                  )}
                </div>
                <div>
                  <FontAwesomeIcon className="mr-2 fa-w-20" icon={faCalendarAlt} />
                  <DateDisplay date={packageDetails.startDate} />
                  <Nbsp />
                  {packageDetails.endDate && (
                    <>
                      {t(Keys.to)} <DateDisplay date={packageDetails.endDate} />
                    </>
                  )}
                </div>
              </>
            )}
          </Col>
          <Col md={4} className="text-right ">
            <div>
              {showTotalPriceOnly && (
                <span className="font-weight-bold pr-1">{t(Keys.totalFrom)}</span>
              )}
              <span className="text-primary font-weight-bold iso__package-details__price">
                {!showTotalPriceOnly && paxPrice && (
                  <Price displayInline align="left" price={paxPrice} />
                )}
                {showTotalPriceOnly && totalPrice && (
                  <Price displayInline align="left" price={totalPrice} />
                )}
              </span>
              {!showTotalPriceOnly && t(Keys.perAdult)}
              <div className="small text-muted">{t(Keys.priceInfo)}</div>
            </div>
          </Col>
        </Row>
        {withMedia(Slider, theme.placeholderImages.package, media, {
          fixedRatio: { x: 16, y: 9 },
          mobileControls: true
        })}
      </>
    );
  };

  const OptionsActionsRow = (): JSX.Element => {
    return (
      <div className="d-flex flex-row justify-content-center justify-content-sm-end align-items-center h6">
        <div className="d-flex flex-row flex-nowrap order-1 order-sm-0">
          <span className="font-weight-bold mr-1">{itemsRenderCount}</span>
          <span>{` ${t(Keys.of)} `}</span>
          <span className="font-weight-bold mx-1">{allOptions && allOptions?.length}</span>
          <span className="mx-2 d-none d-sm-inline-block"> | </span>
        </div>
        <Button
          className="ml-1 order-0 order-sm-1 text-nowrap"
          color="link"
          onClick={(): void => {
            calculateNumberOfOptionsToDisplay(false);
          }}
        >
          {t(Keys.show_less)}
        </Button>

        <Button
          type="button"
          className="mx-1 order-3 order-sm-3 text-nowrap"
          onClick={(): void => {
            calculateNumberOfOptionsToDisplay(true);
          }}
          color="link"
        >
          {t(Keys.show_more)}
        </Button>

        <Button
          type="button"
          className="mr-1 d-none d-sm-inline-block order-sm-4"
          onClick={(): void => {
            if (allOptions) setOptionsPerPage(allOptions.length);
          }}
          color="link"
        >
          {t(Keys.view_all)}
        </Button>
      </div>
    );
  };

  const AlternativeDates = (): JSX.Element => {
    return (
      <div>
        <ScrollTo name="selectedOptions" />
        <Col>
          <Row className="text-uppercase font-weight-bold mb-3">{t(Keys.alternative_dates)}</Row>
          {renderOptions(optionsPerPage)}
          <OptionsActionsRow />
          <hr className="iso__package-details__hr" />
          <Row className="text-uppercase font-weight-bold mt-1 mb-3">
            {t(Keys.chosen_departure)}
          </Row>
          {selectedOption && (
            <Fade className="mb-5">
              <PriceOptionPanel
                travelDate={selectedOption}
                onSelectDate={(): void => gotoCustomizer()}
                key={selectedOption.id}
                showTotalPriceOnly
                isOverviewElement
              />
            </Fade>
          )}
        </Col>
      </div>
    );
  };

  return (
    <div ref={sectionRef}>
      <Header />
      <div className="my-2">
        <PackageDetailsTabs item={item} packageDetails={packageDetails} isSmall={isSmall} />
      </div>
      <div ref={ref} className="mt-6">
        <AlternativeDates />
      </div>
    </div>
  );
});
